import { Plate } from '../components/Card';
import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import api from '../api';

const fmt = v => new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(v || 0);
const fmtCash = v => {
  const abs = new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(Math.abs(v || 0));
  if ((v || 0) > 0) return '+' + abs;
  if ((v || 0) < 0) return '−' + abs;
  return abs;
};
const cashColor = v => (v || 0) > 0 ? '#0e9f6e' : (v || 0) < 0 ? '#e04848' : 'var(--text2)';

function getProjection(turnover, period) {
  if (!period || !turnover) return null;
  const [year, month] = period.split('-').map(Number);
  const daysInMonth = new Date(year, month, 0).getDate();
  const now = new Date();
  const isCurrentMonth = year === now.getFullYear() && month === (now.getMonth() + 1);
  if (!isCurrentMonth) return null;
  const daysPassed = now.getDate();
  if (daysPassed === 0) return null;
  const dailyAvg = turnover / daysPassed;
  const projection = Math.round(dailyAvg * daysInMonth);
  const daysLeft = daysInMonth - daysPassed;
  return {
    projection,
    dailyAvg,
    daysPassed,
    daysInMonth,
    daysLeft,
    projRoyalty: Math.round(projection * 0.05),
    projMarketing: Math.round(projection * 0.05),
  };
}
const fmtShort = v => {
  if (v >= 1000000) return (v / 1000000).toFixed(1) + 'M';
  if (v >= 1000) return (v / 1000).toFixed(0) + 'K';
  return Math.round(v);
};
const COLORS = ['#00a7dc','#0e9f6e','#e08a00','#e04848','#00a7dc','#00a7dc','#00a7dc','#0e9f6e','#e08a00','#0e9f6e','#00a7dc','#e08a00'];

const getPrevPeriod = (periods, period) => {
  const idx = periods.indexOf(period);
  return idx >= 0 && idx < periods.length - 1 ? periods[idx + 1] : null;
};

const MonthArrows = ({ period, periods, onChange }) => {
  const idx = periods.indexOf(period);
  const canOlder = idx >= 0 && idx < periods.length - 1;
  const canNewer = idx > 0;
  const buttonStyle = enabled => ({
    width: 22, height: 22, borderRadius: 7, border: '1px solid var(--border)',
    background: enabled ? 'rgba(0,167,220,0.15)' : 'transparent',
    color: enabled ? '#00a7dc' : 'var(--text2)',
    cursor: enabled ? 'pointer' : 'default',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
    fontSize: 11, lineHeight: 1,
  });
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
      <button type="button" onClick={() => canOlder && onChange(periods[idx + 1])} disabled={!canOlder} style={buttonStyle(canOlder)}>◀</button>
      <span style={{ fontSize: 11, color: 'var(--text2)', whiteSpace: 'nowrap' }}>Пред. месяц</span>
      <button type="button" onClick={() => canNewer && onChange(periods[idx - 1])} disabled={!canNewer} style={buttonStyle(canNewer)}>▶</button>
    </div>
  );
};

const Card = ({ label, value, sub, color = 'var(--accent2)', icon, small }) => (
  <div style={{ background: 'var(--bg2)', borderRadius: 12, padding: '18px 22px', border: '1px solid var(--border)', flex: 1, minWidth: 160 }}>
    <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>{icon} {label}</div>
    <div style={{ fontSize: small ? 22 : 26, fontWeight: 700, color }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4 }}>{sub}</div>}
  </div>
);

const StatusBadge = ({ debt }) => {
  if (debt <= 0) return <span style={{ background: '#0e9f6e20', color: '#0e9f6e', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>✓ Оплачено</span>;
  if (debt < 10000) return <span style={{ background: '#e08a0020', color: '#e08a00', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>⚠ Частично</span>;
  return <span style={{ background: '#e0484820', color: '#e04848', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>✗ Долг</span>;
};

export default function FDM() {
  const [summary, setSummary] = useState(null);
  const [partners, setPartners] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [period, setPeriod] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [expandedCards, setExpandedCards] = useState({});
  const [partnerHistory, setPartnerHistory] = useState({});
  const [partnerCardPeriods, setPartnerCardPeriods] = useState({});

  useEffect(() => {
    api.get('/fdm/periods').then(r => {
      const ps = r.data;
      setPeriods(ps);
      if (ps.length) setPeriod(ps[0]);
    });
  }, []);

  useEffect(() => {
    if (!periods.length) return;
    Promise.all(
      periods.slice(0, 12).map(p =>
        api.get(`/fdm/partners?period=${p}`)
          .then(r => [p, r.data || []])
          .catch(() => [p, []])
      )
    ).then(rows => {
      const next = {};
      rows.forEach(([p, items]) => {
        next[p] = {};
        items.forEach(item => { next[p][item.store_external_id] = item; });
      });
      setPartnerHistory(next);
    });
  }, [periods]);

  const [prevPartnerMap, setPrevPartnerMap] = useState({});
  const [prevSummary, setPrevSummary] = useState(null);
  const [liveStats, setLiveStats] = useState({});
  const [dailyStats, setDailyStats] = useState({});
  const [storeDayIdx, setStoreDayIdx] = useState({});

  useEffect(() => {
    if (!period) return;
    setLoading(true);
    setLiveStats({});
    setDailyStats({});
    const [y, m] = period.split('-').map(Number);
    const prevPeriod = m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
    const now = new Date();
    const isCurrentMonth = y === now.getFullYear() && m === (now.getMonth() + 1);

    // Fast: main data — unblocks the UI immediately
    Promise.all([
      api.get(`/fdm/summary?period=${period}`).then(r => r.data),
      api.get(`/fdm/partners?period=${period}`).then(r => r.data),
      api.get(`/fdm/partners?period=${prevPeriod}`).then(r => r.data).catch(() => []),
      api.get(`/fdm/summary?period=${prevPeriod}`).then(r => r.data).catch(() => null),
    ]).then(([s, p, prevP, prevS]) => {
      setSummary(s);
      setPartners(p);
      setPartnerHistory(prev => ({
        ...prev,
        [period]: Object.fromEntries((p || []).map(item => [item.store_external_id, item])),
        [prevPeriod]: Object.fromEntries((prevP || []).map(item => [item.store_external_id, item])),
      }));
      setPrevSummary(prevS);
      const map = {};
      prevP.forEach(pp => { map[pp.store_external_id] = pp; });
      setPrevPartnerMap(map);
    }).finally(() => setLoading(false));

    // Slow: live and daily stats — load in background without blocking UI
    if (isCurrentMonth) {
      api.get('/fdm/live-stats', { timeout: 120000 }).then(r => {
        setLiveStats(r.data || {});
      }).catch(() => {});

      api.get('/fdm/daily-stats', { timeout: 180000 }).then(r => {
        const daily = r.data || {};
        setDailyStats(daily);
        const today = new Date().toISOString().slice(0, 10);
        const idxMap = {};
        Object.entries(daily).forEach(([sid, sd]) => {
          const days = sd.days || [];
          const ti = days.findIndex(d => d.date === today);
          idxMap[sid] = ti >= 0 ? ti : days.length - 1;
        });
        setStoreDayIdx(idxMap);
      }).catch(() => {});
    }
  }, [period]);

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditData({
      royalty_paid: p.royalty_paid,
      marketing_paid: p.marketing_paid,
      equipment_rent_paid: p.equipment_rent_paid,
      space_rent_paid: p.space_rent_paid,
      space_rent: p.space_rent,
      franchise_fee: p.franchise_fee,
      franchise_fee_paid: p.franchise_fee_paid,
      moysklad_fee_paid: p.moysklad_fee_paid,
      comm_fee_paid: p.comm_fee_paid,
      accounting_fee_paid: p.accounting_fee_paid,
      card_fee_paid: p.card_fee_paid,
      batrek_fee_paid: p.batrek_fee_paid,
      telematics_fee_paid: p.telematics_fee_paid,
      notes: p.notes,
    });
  };

  const saveEdit = async (id) => {
    await api.patch(`/fdm/partners/${id}`, editData);
    setEditingId(null);
    const [s, p] = await Promise.all([
      api.get(`/fdm/summary?period=${period}`).then(r => r.data),
      api.get(`/fdm/partners?period=${period}`).then(r => r.data),
    ]);
    setSummary(s);
    setPartners(p);
  };

  const markAllPaid = async (partner) => {
    const d = {
      royalty_paid: partner.royalty_expected,
      marketing_paid: partner.marketing_expected,
      equipment_rent_paid: partner.equipment_rent,
      space_rent_paid: partner.space_rent,
      franchise_fee_paid: partner.franchise_fee,
      moysklad_fee_paid: partner.moysklad_fee,
      comm_fee_paid: partner.comm_fee,
      accounting_fee_paid: partner.accounting_fee,
      card_fee_paid: partner.card_fee,
      batrek_fee_paid: partner.batrek_fee,
      telematics_fee_paid: partner.telematics_fee,
    };
    await api.patch(`/fdm/partners/${partner.id}`, d);
    const [s, p] = await Promise.all([
      api.get(`/fdm/summary?period=${period}`).then(r => r.data),
      api.get(`/fdm/partners?period=${period}`).then(r => r.data),
    ]);
    setSummary(s);
    setPartners(p);
  };

  const topChart = [...partners]
    .slice(0, 12)
    .map(p => ({ name: p.name.length > 20 ? p.name.slice(0, 20) + '…' : p.name, turnover: p.turnover, income: p.total_expected }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', fontSize: 12 }}>
        <div style={{ fontWeight: 600, marginBottom: 6, wordBreak: 'break-word', maxWidth: 200 }}>{label}</div>
        {payload.map(p => <div key={p.dataKey} style={{ color: p.color }}>{p.name}: {fmt(p.value)}</div>)}
      </div>
    );
  };

  return (
    <div>
      <div className="kp-hero" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <Plate>Франшиза · ФДМ</Plate>
          <h1 style={{ margin: 0 }}>ФДМ — франшиза для мастера</h1>
          <div style={{ color: 'var(--text2)', fontSize: 13, marginTop: 8 }}>
            Мои точки, сданные мастерам · СПб, {partners.length || 13} партнёров
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
            {[
              { label: `Роялти ${summary?.royalty_rate || 5}%`, sub: '→ ООО БОБ', color: '#00a7dc' },
              { label: `Маркетинг ${summary?.marketing_rate || 5}%`, sub: '→ ООО БОБ', color: '#00a7dc' },
              { label: '50 000 ₽/мес', sub: 'аренда оборудования', color: '#e08a00' },
              { label: 'Аренда помещения', sub: 'мастер → арендодателю', color: '#0e9f6e' },
              { label: 'Паушальный взнос', sub: 'рассрочка', color: '#e08a00' },
            ].map(b => (
              <span key={b.label} style={{
                background: `${b.color}18`, border: `1px solid ${b.color}40`,
                borderRadius: 8, padding: '4px 10px', fontSize: 11,
                color: b.color, fontWeight: 600,
              }}>{b.label} <span style={{ opacity: 0.7, fontWeight: 400 }}>{b.sub}</span></span>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {periods.slice(0, 4).map(p => (
            <button key={p} onClick={() => setPeriod(p)} style={{
              padding: '6px 14px', borderRadius: 6, border: '1px solid var(--border)',
              background: period === p ? 'var(--accent)' : 'var(--bg2)',
              color: period === p ? '#fff' : 'var(--text2)', cursor: 'pointer', fontSize: 13,
            }}>{p}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 80, color: 'var(--text2)' }}>⏳ Загрузка...</div>
      ) : (
        <>
          {/* KPI */}
          <div style={{ display: 'flex', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
            <Card icon="🏪" label="Партнёров ФДМ" value={summary?.partners_count} sub="активных в периоде" color="#00a7dc" />
            <Card icon="💰" label="Суммарный оборот" value={fmt(summary?.total_turnover)} sub={(() => {
              if (!prevSummary?.total_turnover) return 'по кассам партнёров';
              const totalProj = getProjection(summary?.total_turnover, period);
              const cmp = totalProj ? totalProj.projection : summary?.total_turnover;
              const d = ((cmp - prevSummary.total_turnover) / prevSummary.total_turnover * 100);
              return `${totalProj ? 'Тренд' : 'Факт'}: ${d >= 0 ? '↑' : '↓'}${Math.abs(d).toFixed(1)}% vs ${fmt(prevSummary.total_turnover)}`;
            })()} color="#0e9f6e" />
            <Card icon="📥" label="Платёж в УК" value={fmt(summary?.total_expected)} sub={`Роялти + маркетинг + аренда`} color="#00a7dc" />
            <Card icon="✅" label="Фактически получено" value={fmt(summary?.total_paid)} color="#0e9f6e" small />
            <Card icon="🔴" label="Долг партнёров" value={fmt(summary?.total_debt)} color={summary?.total_debt > 0 ? '#e04848' : '#0e9f6e'} small />
          </div>

          {/* Breakdown cards */}
          <div style={{ display: 'flex', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
            <div style={{ background: 'var(--bg2)', borderRadius: 12, padding: '16px 20px', border: '1px solid var(--border)', flex: 1 }}>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>📊 Роялти 5%</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#00a7dc' }}>{fmt(summary?.total_royalty_expected)}</div>
            </div>
            <div style={{ background: 'var(--bg2)', borderRadius: 12, padding: '16px 20px', border: '1px solid var(--border)', flex: 1 }}>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>📣 Маркетинг 5%</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#e08a00' }}>{fmt(summary?.total_marketing_expected)}</div>
            </div>
            <div style={{ background: 'var(--bg2)', borderRadius: 12, padding: '16px 20px', border: '1px solid var(--border)', flex: 1 }}>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>🖥️ Аренда оборудования</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#00a7dc' }}>{fmt(summary?.total_equipment_rent)}</div>
              <div style={{ fontSize: 11, color: 'var(--text2)' }}>{summary?.partners_count} × 50 000 ₽</div>
            </div>
            {summary?.total_space_rent > 0 && (
              <div style={{ background: 'var(--bg2)', borderRadius: 12, padding: '16px 20px', border: '1px solid var(--border)', flex: 1 }}>
                <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>🏢 Аренда помещений</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#0e9f6e' }}>{fmt(summary?.total_space_rent)}</div>
              </div>
            )}
          </div>

          {/* Chart */}
          {topChart.length > 0 && (
            <div style={{ background: 'var(--bg2)', borderRadius: 12, padding: '20px 24px', border: '1px solid var(--border)', marginBottom: 24 }}>
              <div style={{ fontWeight: 600, marginBottom: 16 }}>📊 Оборот партнёров</div>
              <ResponsiveContainer width="100%" height={Math.max(260, topChart.length * 36)}>
                <BarChart data={topChart} layout="vertical" margin={{ top: 4, right: 80, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                  <XAxis type="number" tickFormatter={fmtShort} tick={{ fontSize: 11, fill: 'var(--text2)' }} />
                  <YAxis type="category" dataKey="name" width={190} tick={{ fontSize: 11, fill: 'var(--text1)' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="turnover" name="Оборот" radius={[0, 4, 4, 0]}>
                    {topChart.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Partners cards — retail style */}
          <div style={{ fontWeight: 600, marginBottom: 14, fontSize: 15 }}>📋 Партнёры</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
            {partners.map((p, idx) => {
              const cardPeriod = partnerCardPeriods[p.store_external_id] || period;
              const monthPartner = partnerHistory[cardPeriod]?.[p.store_external_id];
              const displayPartner = monthPartner || (cardPeriod === period ? p : null);
              const turnover = displayPartner?.turnover || 0;
              const totalExpected = displayPartner?.total_expected || 0;
              const totalPaid = displayPartner?.total_paid || 0;
              const debt = displayPartner?.debt || 0;
              const prevCardPeriod = getPrevPeriod(periods, cardPeriod);
              const prev = prevCardPeriod ? partnerHistory[prevCardPeriod]?.[p.store_external_id] : prevPartnerMap[p.store_external_id];
              const prevTurnover = prev?.turnover || 0;
              const periodTotal = Object.values(partnerHistory[cardPeriod] || {}).reduce((sum, item) => sum + (item.turnover || 0), 0);
              const proj = getProjection(turnover, cardPeriod);
              const color = COLORS[idx % COLORS.length];
              const shareOfTotal = periodTotal ? (turnover / periodTotal * 100) : 0;
              const trendValue = proj ? proj.projection : turnover;
              const trendDelta = prevTurnover > 0 ? ((trendValue - prevTurnover) / prevTurnover * 100) : null;
              return (
                <div key={p.id} style={{
                  background: 'var(--bg2)', borderRadius: 12, padding: '16px 20px',
                  border: '1px solid var(--border)', borderLeft: `4px solid ${color}`,
                  transition: 'transform 0.1s', cursor: 'default',
                }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  {/* Заголовок + кнопки */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color, lineHeight: 1.3, flex: 1, marginRight: 8 }}>{p.name}</div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                      <StatusBadge debt={debt} />
                    </div>
                  </div>

                  {/* Метрики */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--text2)' }}>Оборот</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#0e9f6e' }}>{fmt(turnover)}</div>
                    </div>
                    <div>
                      <MonthArrows
                        period={cardPeriod}
                        periods={periods}
                        onChange={nextPeriod => setPartnerCardPeriods(prev => ({ ...prev, [p.store_external_id]: nextPeriod }))}
                      />
                      <div style={{ fontSize: 18, fontWeight: 700, color: prevTurnover > 0 ? 'var(--text1)' : 'var(--text2)' }}>
                        {prevTurnover > 0 ? fmt(prevTurnover) : '—'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--text2)' }}>Платёж в УК</div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: '#00a7dc' }}>{fmt(totalExpected)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--text2)' }}>Получено</div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: totalPaid >= totalExpected && totalExpected > 0 ? '#0e9f6e' : '#e08a00' }}>{fmt(totalPaid)}</div>
                    </div>
                  </div>

                  {/* Навигатор по дням: касса, выручка нал/безнал */}
                  {(() => {
                    const sid = p.store_external_id;
                    const sd = dailyStats[sid];
                    const ls = liveStats[sid];
                    const cashBalance = sd?.cash_balance ?? ls?.cash_balance ?? null;
                    const days = sd?.days || [];
                    const today = new Date().toISOString().slice(0, 10);
                    const idx = storeDayIdx[sid] ?? (days.length - 1);
                    if (!days.length && cashBalance === null) return null;
                    const day = days[idx] || { date: today, revenue: 0, cash: 0, noncash: 0, count: 0 };
                    const isToday = day.date === today;
                    const dayLabel = day.date ? new Date(day.date + 'T12:00:00').toLocaleDateString('ru-RU', { weekday: 'short', day: 'numeric', month: 'short' }) : '';
                    const canPrev = idx > 0;
                    const canNext = idx < days.length - 1;
                    const setIdx = (v) => setStoreDayIdx(prev => ({ ...prev, [sid]: v }));

                    return (
                      <div style={{
                        marginBottom: 10, padding: '10px 12px', borderRadius: 8,
                        background: 'rgba(0,167,220,0.06)', border: '1px solid rgba(0,167,220,0.2)',
                      }}>
                        {/* Касса */}
                        {cashBalance !== null && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                            <span style={{ fontSize: 10, color: cashColor(cashBalance), textTransform: 'uppercase', fontWeight: 600 }}>💰 В кассе</span>
                            <span style={{ fontSize: 16, fontWeight: 800, color: cashColor(cashBalance) }}>{fmtCash(cashBalance)}</span>
                          </div>
                        )}

                        {/* Навигация по дню */}
                        {days.length > 0 && (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
                              <button onClick={() => canPrev && setIdx(idx - 1)} disabled={!canPrev} style={{
                                background: canPrev ? 'rgba(0,167,220,0.15)' : 'transparent',
                                color: canPrev ? '#00a7dc' : 'var(--text2)',
                                border: '1px solid var(--border)', borderRadius: 8,
                                width: 28, height: 28, fontSize: 14, cursor: canPrev ? 'pointer' : 'default',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                              }}>◀</button>
                              <div style={{ textAlign: 'center', minWidth: 100 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text1)' }}>{dayLabel}</div>
                                <div style={{ fontSize: 10, color: isToday ? '#0e9f6e' : 'var(--text2)', fontWeight: 600 }}>
                                  {isToday ? '● сегодня' : day.date}
                                </div>
                              </div>
                              <button onClick={() => canNext && setIdx(idx + 1)} disabled={!canNext} style={{
                                background: canNext ? 'rgba(0,167,220,0.15)' : 'transparent',
                                color: canNext ? '#00a7dc' : 'var(--text2)',
                                border: '1px solid var(--border)', borderRadius: 8,
                                width: 28, height: 28, fontSize: 14, cursor: canNext ? 'pointer' : 'default',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                              }}>▶</button>
                            </div>

                            {/* Выручка: всего, нал, безнал */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                              <div style={{ background: 'rgba(14,159,110,0.1)', borderRadius: 6, padding: '6px 8px', textAlign: 'center' }}>
                                <div style={{ fontSize: 9, color: 'var(--text2)', textTransform: 'uppercase' }}>Выручка</div>
                                <div style={{ fontSize: 15, fontWeight: 800, color: '#0e9f6e' }}>{fmt(day.revenue)}</div>
                              </div>
                              <div style={{ background: 'rgba(14,159,110,0.08)', borderRadius: 6, padding: '6px 8px', textAlign: 'center' }}>
                                <div style={{ fontSize: 9, color: 'var(--text2)', textTransform: 'uppercase' }}>💵 Нал</div>
                                <div style={{ fontSize: 14, fontWeight: 700, color: '#0e9f6e' }}>{fmt(day.cash)}</div>
                              </div>
                              <div style={{ background: 'rgba(0,167,220,0.08)', borderRadius: 6, padding: '6px 8px', textAlign: 'center' }}>
                                <div style={{ fontSize: 9, color: 'var(--text2)', textTransform: 'uppercase' }}>💳 Безнал</div>
                                <div style={{ fontSize: 14, fontWeight: 700, color: '#00a7dc' }}>{fmt(day.noncash)}</div>
                              </div>
                            </div>

                            {/* Кол-во чеков */}
                            <div style={{ textAlign: 'center', marginTop: 6, fontSize: 11, color: 'var(--text2)' }}>
                              🧾 {day.count} {day.count === 1 ? 'чек' : day.count < 5 ? 'чека' : 'чеков'}
                              {day.count > 0 && <span style={{ marginLeft: 8 }}>· ср. чек {fmt(Math.round(day.revenue / day.count))}</span>}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })()}

                  {/* Тренд vs прошлый месяц */}
                  {prevTurnover > 0 && trendDelta !== null && (
                    <div style={{
                      marginBottom: 10, padding: '8px 12px', borderRadius: 8,
                      background: trendDelta >= 0 ? 'rgba(14,159,110,0.08)' : 'rgba(224,72,72,0.08)',
                      border: `1px solid ${trendDelta >= 0 ? 'rgba(14,159,110,0.25)' : 'rgba(224,72,72,0.25)'}`,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 11, color: 'var(--text2)' }}>{proj ? '📈 Тренд vs пред. месяц' : 'vs пред. месяц'}</span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: trendDelta >= 0 ? '#0e9f6e' : '#e04848' }}>
                          {trendDelta >= 0 ? '↑' : '↓'} {Math.abs(trendDelta).toFixed(1)}%
                        </span>
                      </div>
                      {proj && (
                        <div style={{ fontSize: 10, color: 'var(--text2)', marginTop: 3 }}>
                          Прогноз {fmt(proj.projection)} vs {fmt(prevTurnover)}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Прогноз */}
                  {proj && (
                    <div style={{
                      marginBottom: 10, padding: '8px 10px', borderRadius: 8,
                      background: 'rgba(0,167,220,0.08)', border: '1px solid rgba(0,167,220,0.2)',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                        <span style={{ fontSize: 11, color: 'var(--text2)' }}>📈 Прогноз оборота на месяц</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#00a7dc' }}>{fmt(proj.projection)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text2)', marginBottom: 4 }}>
                        <span>Роялти ~{fmt(proj.projRoyalty)} · Мркт ~{fmt(proj.projMarketing)}</span>
                        <span>{fmt(Math.round(proj.dailyAvg))}/день · {proj.daysPassed}/{proj.daysInMonth} дн.</span>
                      </div>
                      <div style={{ height: 3, background: 'var(--bg3)', borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${(proj.daysPassed / proj.daysInMonth) * 100}%`, background: '#00a7dc', borderRadius: 2 }} />
                      </div>
                    </div>
                  )}

                  {/* Доля в обороте */}
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text2)', marginBottom: 4 }}>
                      <span>Доля в общем обороте</span>
                      <span>{shareOfTotal.toFixed(1)}%</span>
                    </div>
                    <div style={{ height: 4, background: 'var(--bg3)', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${Math.min(shareOfTotal, 100)}%`, background: color, borderRadius: 2 }} />
                    </div>
                  </div>

                  {/* Платежи — раскрывающийся блок */}
                  <div style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                    <button
                      onClick={() => setExpandedCards(prev => ({ ...prev, [p.id]: !prev[p.id] }))}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
                        background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0', color: 'var(--text2)',
                      }}
                    >
                      <span style={{ fontSize: 12, fontWeight: 600 }}>💳 Платежи партнёра</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {debt > 0 && <span style={{ fontSize: 11, color: '#e04848', fontWeight: 600 }}>Долг {fmt(debt)}</span>}
                        {debt <= 0 && totalExpected > 0 && <span style={{ fontSize: 11, color: '#0e9f6e', fontWeight: 600 }}>Оплачено</span>}
                        <span style={{
                          display: 'inline-block', transition: 'transform 0.2s',
                          transform: expandedCards[p.id] ? 'rotate(180deg)' : 'rotate(0)',
                          fontSize: 14,
                        }}>▾</span>
                      </span>
                    </button>

                    {expandedCards[p.id] && (
                      <div style={{ marginTop: 8 }}>
                        {editingId !== p.id && (
                          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                            <button onClick={() => markAllPaid(p)} style={{
                              flex: 1, padding: '7px 10px', borderRadius: 7, border: 'none',
                              background: '#0e9f6e20', color: '#0e9f6e', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                            }}>✓ Всё оплачено</button>
                            <button onClick={() => startEdit(p)} style={{
                              flex: 1, padding: '7px 10px', borderRadius: 7, border: '1px solid var(--border)',
                              background: 'transparent', color: 'var(--text2)', cursor: 'pointer', fontSize: 12,
                            }}>✏️ Ввести платежи</button>
                          </div>
                        )}

                        {editingId === p.id ? (
                          <div>
                            <div style={{ fontSize: 11, color: 'var(--text2)', fontWeight: 600, marginBottom: 6 }}>Основные</div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 10 }}>
                              {[
                                { key: 'royalty_paid', label: `Роялти (${fmt(p.royalty_expected)})` },
                                { key: 'marketing_paid', label: `Маркетинг (${fmt(p.marketing_expected)})` },
                                { key: 'equipment_rent_paid', label: `Оборудование (${fmt(p.equipment_rent)})` },
                                { key: 'space_rent_paid', label: `Аренда помещ. (${fmt(p.space_rent)})` },
                                { key: 'franchise_fee_paid', label: `Паушальный (${fmt(p.franchise_fee)})` },
                              ].map(f => (
                                <div key={f.key}>
                                  <label style={{ fontSize: 11, color: 'var(--text2)', display: 'block', marginBottom: 4 }}>{f.label}</label>
                                  <input type="number" value={editData[f.key] || ''} onChange={e => setEditData({ ...editData, [f.key]: parseFloat(e.target.value) || 0 })}
                                    style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text1)', fontSize: 13, boxSizing: 'border-box' }} />
                                </div>
                              ))}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--text2)', fontWeight: 600, marginBottom: 6 }}>Сервисные</div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 10 }}>
                              {[
                                { key: 'moysklad_fee_paid', label: `МойСклад (${fmt(p.moysklad_fee)})` },
                                { key: 'comm_fee_paid', label: `Связь (${fmt(p.comm_fee)})` },
                                { key: 'accounting_fee_paid', label: `Бухгалтерия (${fmt(p.accounting_fee)})` },
                                { key: 'card_fee_paid', label: `Карты (${fmt(p.card_fee)})` },
                                { key: 'batrek_fee_paid', label: `Bitrix24 (${fmt(p.batrek_fee)})` },
                                { key: 'telematics_fee_paid', label: `Телематика (${fmt(p.telematics_fee)})` },
                              ].map(f => (
                                <div key={f.key}>
                                  <label style={{ fontSize: 11, color: 'var(--text2)', display: 'block', marginBottom: 4 }}>{f.label}</label>
                                  <input type="number" value={editData[f.key] || ''} onChange={e => setEditData({ ...editData, [f.key]: parseFloat(e.target.value) || 0 })}
                                    style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text1)', fontSize: 13, boxSizing: 'border-box' }} />
                                </div>
                              ))}
                            </div>
                            <div style={{ marginBottom: 10 }}>
                              <label style={{ fontSize: 11, color: 'var(--text2)', display: 'block', marginBottom: 4 }}>Комментарий</label>
                              <input type="text" value={editData.notes || ''} onChange={e => setEditData({ ...editData, notes: e.target.value })}
                                style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text1)', fontSize: 13, boxSizing: 'border-box' }} />
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button onClick={() => saveEdit(p.id)} style={{ padding: '7px 18px', borderRadius: 7, border: 'none', background: '#0e9f6e', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>Сохранить</button>
                              <button onClick={() => setEditingId(null)} style={{ padding: '7px 18px', borderRadius: 7, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text2)', cursor: 'pointer' }}>Отмена</button>
                            </div>
                          </div>
                        ) : (
                          <div>
                            {[
                              { label: 'Роялти', exp: p.royalty_expected, paid: p.royalty_paid, color: '#00a7dc' },
                              { label: 'Маркетинг', exp: p.marketing_expected, paid: p.marketing_paid, color: '#e08a00' },
                              { label: 'Аренда оборуд.', exp: p.equipment_rent, paid: p.equipment_rent_paid, color: '#00a7dc' },
                              { label: 'Аренда помещ.', exp: p.space_rent, paid: p.space_rent_paid, color: '#0e9f6e', rentNote: p.rent_note },
                              { label: 'Паушальный', exp: p.franchise_fee, paid: p.franchise_fee_paid, color: '#e08a00', franchiseStatus: p.franchise_status },
                              { label: 'МойСклад', exp: p.moysklad_fee, paid: p.moysklad_fee_paid, color: '#00a7dc' },
                              { label: 'Связь', exp: p.comm_fee, paid: p.comm_fee_paid, color: '#0e9f6e' },
                              { label: 'Бухгалтерия', exp: p.accounting_fee, paid: p.accounting_fee_paid, color: '#00a7dc' },
                              { label: 'Выдача карт', exp: p.card_fee, paid: p.card_fee_paid, color: '#00a7dc' },
                              { label: 'Bitrix24', exp: p.batrek_fee, paid: p.batrek_fee_paid, color: '#e08a00' },
                              { label: 'Телематика', exp: p.telematics_fee, paid: p.telematics_fee_paid, color: 'var(--text2)' },
                            ].filter(item => item.exp > 0 || item.rentNote || item.franchiseStatus).map(item => (
                              <div key={item.label} style={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                padding: '6px 0', borderBottom: '1px solid var(--border)',
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <div style={{ width: 3, height: 20, borderRadius: 2, background: item.color, flexShrink: 0 }} />
                                  <div>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text1)' }}>{item.label}</div>
                                    {item.franchiseStatus === 'paid' && item.exp <= 0 ? (
                                      <span style={{ fontSize: 10, color: '#0e9f6e', fontWeight: 500 }}>Оплачено</span>
                                    ) : item.rentNote === 'direct' ? (
                                      <span style={{ fontSize: 10, color: '#0e9f6e', fontWeight: 500 }}>Напрямую</span>
                                    ) : (
                                      <span style={{ fontSize: 10, color: 'var(--text2)' }}>{fmt(item.exp)}</span>
                                    )}
                                  </div>
                                </div>
                                {item.franchiseStatus === 'paid' && item.exp <= 0 ? (
                                  <span style={{ background: '#0e9f6e18', color: '#0e9f6e', padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600 }}>✓ Оплачено</span>
                                ) : item.rentNote === 'direct' ? (
                                  <span style={{ background: '#0e9f6e18', color: '#0e9f6e', padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600 }}>Прямая</span>
                                ) : (
                                  <div style={{ textAlign: 'right' }}>
                                    <div style={{
                                      fontSize: 13, fontWeight: 700,
                                      color: item.paid >= item.exp && item.exp > 0 ? '#0e9f6e' : (item.paid > 0 ? '#e08a00' : 'var(--text2)'),
                                    }}>{fmt(item.paid)}</div>
                                    {item.exp > 0 && (
                                      <div style={{ width: 50, height: 3, background: 'var(--border)', borderRadius: 2, marginTop: 2, marginLeft: 'auto' }}>
                                        <div style={{ height: '100%', width: `${Math.min(item.paid / item.exp * 100, 100)}%`, background: item.paid >= item.exp ? '#0e9f6e' : '#e08a00', borderRadius: 2 }} />
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                            <div style={{
                              display: 'flex', justifyContent: 'space-between', padding: '8px 0',
                              fontSize: 13, fontWeight: 700, color: 'var(--text1)',
                            }}>
                              <span>Итого</span>
                              <span>{fmt(p.total_paid)} / {fmt(p.total_expected)}</span>
                            </div>
                          </div>
                        )}

                        {p.notes && (
                          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text2)', fontStyle: 'italic' }}>💬 {p.notes}</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
